import React, { Children, cloneElement, forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    FloatingPortal,
    autoUpdate,
    flip,
    offset,
    shift,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useMergeRefs,
    useRole,
} from '@floating-ui/react';

// MUI Tooltip in the app theme: chrome background and divider, 6px radius,
// 10px text, 300px wide at most, 14px from its anchor. Under the default MSI
// Clean palette the chrome is navy with white text; every other theme
// derives its chrome text from the text colour.
const TOOLTIP_CLASS_NAME = 'z-[1500] max-w-[300px] rounded-md border border-solid border-ui-tooltip-line '
    + 'bg-ui-tooltip px-2 py-1 text-[10px] font-medium leading-[1.4] text-content-primary '
    + 'shadow-[0_6px_16px_rgba(12,23,38,0.16)] [overflow-wrap:break-word] '
    + '[html[data-theme=msiClean]_&]:text-white';

// The anchor's own props with those a wrapping Menu or Popover passed to the
// Tooltip; handlers both define run in turn, anchor first.
function mergeAnchorProps(own, passed) {
    const merged = { ...own, ...passed };
    Object.keys(passed).forEach((key) => {
        if (/^on[A-Z]/.test(key) && typeof own[key] === 'function' && typeof passed[key] === 'function') {
            merged[key] = (...args) => {
                own[key](...args);
                passed[key](...args);
            };
        }
    });
    return merged;
}

/**
 * A tooltip that opens while its anchor is hovered (after `delay` ms) or
 * focused, and closes on leave, blur or Escape. The anchor is `children`:
 * one element that takes a ref (a DOM element or a forwardRef component).
 * It gets the event handlers and, while the tooltip shows, an
 * `aria-describedby` pointing at it. Empty `content` shows nothing, so a
 * caller can decide at hover time whether there is anything to say.
 *
 * A Tooltip can itself be the trigger of a Menu or Popover: the ref and
 * props they give it pass through to the anchor.
 */
const Tooltip = forwardRef(function Tooltip({
    content,
    placement = 'bottom',
    delay = 100,
    children,
    ...anchorProps
}, forwardedRef) {
    const [open, setOpen] = useState(false);
    const hasContent = content != null && content !== false && content !== '';
    const visible = open && hasContent;
    const { refs, floatingStyles, context } = useFloating({
        open: visible,
        onOpenChange: setOpen,
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [offset(14), flip(), shift({ padding: 8 })],
    });
    const hover = useHover(context, { move: false, delay: { open: delay, close: 0 } });
    const focus = useFocus(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'tooltip' });
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);
    const anchor = Children.only(children);
    const anchorRef = useMergeRefs([refs.setReference, anchor.ref, forwardedRef]);
    return (
        <>
            {cloneElement(anchor, getReferenceProps({ ...mergeAnchorProps(anchor.props, anchorProps), ref: anchorRef }))}
            {visible && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        className={TOOLTIP_CLASS_NAME}
                        {...getFloatingProps()}
                    >
                        {content}
                    </div>
                </FloatingPortal>
            )}
        </>
    );
});

Tooltip.propTypes = {
    content: PropTypes.node,
    placement: PropTypes.string,
    delay: PropTypes.number,
    children: PropTypes.element.isRequired,
};

export default Tooltip;
